/**
 * Web3 Configuration
 * 
 * Configures wagmi and Reown AppKit for wallet connection.
 * Part of the Mango Group ecosystem using $MANGO token.
 */

import { createAppKit } from "@reown/appkit/react";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { base, baseSepolia, mainnet, sepolia, arbitrum, polygon } from "wagmi/chains";

// Project metadata for wallet connection
const PROJECT_NAME = "Listo Marketplace";
const PROJECT_DESCRIPTION = "Modern classifieds marketplace - Mango Group ecosystem";
const PROJECT_URL = typeof window !== "undefined" ? window.location.origin : "https://listomarket.app";
const PROJECT_ICON = "/logo.png";

// WalletConnect Project ID (get from https://cloud.walletconnect.com)
// TODO: Replace with actual project ID
const WALLET_CONNECT_PROJECT_ID = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || "demo";

/**
 * Supported chains configuration
 * Primary: Base (low fees, fast)
 * Secondary: Ethereum mainnet, Arbitrum, Polygon
 */
export const supportedChains = [
  base,
  mainnet,
  arbitrum,
  polygon,
  // Testnets (only in development)
  ...(import.meta.env.DEV ? [baseSepolia, sepolia] : []),
] as const;

/**
 * $MANGO token contract addresses per chain
 */
export const MANGO_CONTRACTS = {
  // Mainnet addresses (TODO: Add actual deployed addresses)
  [mainnet.id]: {
    token: "0x0000000000000000000000000000000000000000", // $MANGO token
    staking: "0x0000000000000000000000000000000000000000", // Staking contract
    escrow: "0x0000000000000000000000000000000000000000", // Escrow contract
    feeRouter: "0x0000000000000000000000000000000000000000", // Fee router
  },
  [base.id]: {
    token: "0x0000000000000000000000000000000000000000",
    staking: "0x0000000000000000000000000000000000000000",
    escrow: "0x0000000000000000000000000000000000000000",
    feeRouter: "0x0000000000000000000000000000000000000000",
  },
  [arbitrum.id]: {
    token: "0x0000000000000000000000000000000000000000",
    staking: "0x0000000000000000000000000000000000000000",
    escrow: "0x0000000000000000000000000000000000000000",
    feeRouter: "0x0000000000000000000000000000000000000000",
  },
  [polygon.id]: {
    token: "0x0000000000000000000000000000000000000000",
    staking: "0x0000000000000000000000000000000000000000",
    escrow: "0x0000000000000000000000000000000000000000",
    feeRouter: "0x0000000000000000000000000000000000000000",
  },
  // Testnets
  [baseSepolia.id]: {
    token: "0x0000000000000000000000000000000000000000",
    staking: "0x0000000000000000000000000000000000000000",
    escrow: "0x0000000000000000000000000000000000000000",
    feeRouter: "0x0000000000000000000000000000000000000000",
  },
  [sepolia.id]: {
    token: "0x0000000000000000000000000000000000000000",
    staking: "0x0000000000000000000000000000000000000000",
    escrow: "0x0000000000000000000000000000000000000000",
    feeRouter: "0x0000000000000000000000000000000000000000",
  },
} as const;

// 1. Create Wagmi Adapter
export const wagmiAdapter = new WagmiAdapter({
  networks: supportedChains as any,
  projectId: WALLET_CONNECT_PROJECT_ID,
  ssr: true
});

export const wagmiConfig = wagmiAdapter.wagmiConfig;

// 2. Create AppKit
createAppKit({
  adapters: [wagmiAdapter],
  networks: supportedChains as any,
  projectId: WALLET_CONNECT_PROJECT_ID,
  metadata: {
    name: PROJECT_NAME,
    description: PROJECT_DESCRIPTION,
    url: PROJECT_URL,
    icons: [PROJECT_ICON]
  },
  features: {
    analytics: WALLET_CONNECT_PROJECT_ID !== "demo", // Disable analytics if using demo key
    email: false, 
    socials: [],
  },
  enableWalletConnect: true,
  enableInjected: true,
  enableEIP6963: true,
  themeMode: 'light', 
  themeVariables: {
    '--w3m-accent': '#f97316', 
    '--w3m-border-radius-master': '1px'
  }
});

/**
 * Get contract addresses for current chain
 */
export function getContractAddresses(chainId: number) {
  return MANGO_CONTRACTS[chainId as keyof typeof MANGO_CONTRACTS] ?? null;
}

/**
 * Supported chain ID type
 */
export type SupportedChainId = keyof typeof MANGO_CONTRACTS;

/**
 * Check if a chain is supported
 */
export function isSupportedChain(chainId: number): chainId is SupportedChainId {
  return chainId in MANGO_CONTRACTS;
}

/**
 * Get chain info by ID
 */
export function getChainInfo(chainId: number) {
  return supportedChains.find((chain) => chain.id === chainId);
}

// Re-export chain info for convenience
export { base, baseSepolia, mainnet, sepolia, arbitrum, polygon };
